import type { DagTaskStatus } from '@/entities/dag/lib/constants';

export type DagDetailTabKey = 'Task' | 'Execution Record' | 'Code';

export type DagApiError = {
  message: string;
  detail: string;
  error_code: string;
};

export type LibraryItem = {
  name: string;
  preinstalled: boolean;
  libraryVersion: string;
};

export const DAG_MATURITY_TYPES = {
  BRONZE: 'bronze',
  SILVER: 'silver',
  GOLD: 'gold',
} as const;

export type DagMaturity = (typeof DAG_MATURITY_TYPES)[keyof typeof DAG_MATURITY_TYPES];

export const DAG_LIST_RUN_STATUS_TYPES = {
  DRAFT: 'draft',
  FAILURE: 'failure',
  PENDING: 'pending',
  RUNNING: 'running',
  SUCCESS: 'success',
} as const;

export type AirflowTaskInstanceState =
  | 'removed'
  | 'scheduled'
  | 'queued'
  | 'running'
  | 'success'
  | 'restarting'
  | 'failed'
  | 'up_for_retry'
  | 'up_for_reschedule'
  | 'upstream_failed'
  | 'skipped'
  | 'deferred'
  | null;

export type DagListRunStatus = (typeof DAG_LIST_RUN_STATUS_TYPES)[keyof typeof DAG_LIST_RUN_STATUS_TYPES];

export type AirflowDagRunState = 'failed' | 'queued' | 'running' | 'success';
export type AirflowDagListRunStatus = AirflowDagRunState | DagListRunStatus;

export type DagVersionSnapshot = {
  versionLabel: string;
  description?: string;
  timetableSummary?: string;
  runStatus?: DagListRunStatus;
  lastRunAt?: string;
  nextDagrunRunAfter?: string;
  maturity?: DagMaturity;
};

export type DagCodeVersion = {
  versionLabel: string;
  bodyCode: string;
  importedLibraries: ImportedLibrary[];
  availableLibraries?: LibraryItem[];
  installedLibraries?: DagInstalledLibrary[];
  deployedAt?: string;
};

export type GlobalLibraryListRequest = Record<string, never>;

export type GlobalLibraryListResponse = {
  items: LibraryItem[];
  total: number;
};

export type DagImmutableCodeResponse = {
  code: string;
};

export type UploadLibraryRequest = {
  filename: string;
  content: string;
};

export type UploadLibraryResponse = {
  name: string;
  message: string;
};

// DAG Dashboard List API (GET /api/v1/dags/) 요청 타입
export type DagDashboardListParams = {
  keyword?: string;
  offset?: number;
  limit?: number;
  status?: DagListRunStatus;
  tag?: string;
  maturity?: string;
  isBookmarked?: boolean;
  sortBy?: 'name' | 'next_run' | 'last_run';
  sortOrder?: 'asc' | 'desc';
};

export type DagVersionStatusItem = {
  version: number;
  status?: DagListRunStatus | null;
  lastRun?: string | null;
};

export type DagDashboardResponse = {
  dagUuid: string;
  name: string;
  description?: string | null;
  maturity: DagMaturity;
  entityType?: string | null;
  dataSourceId?: string | null;
  isSchedulingEnabled: boolean;
  hasDeployment: boolean;
  deployedVersion?: number | null;
  latestVersion?: number | null;
  schedule?: string | null;
  nextRun?: string | null;
  versions?: DagVersionStatusItem[];
  tags?: string[];
  isBookmarked?: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type DagDashboardListResponse = {
  items: DagDashboardResponse[];
  total: number;
};

// DAG Dashboard Detail API (GET /api/v1/dags/{dag_uuid}) 요청 타입
export type DagDashboardDetailRequest = {
  dagUuid: string;
};

export type UpdateDagRequest = {
  dagUuid: string;
  description?: string | null;
  tags?: string[] | null;
};

export type UpdateDagResponse = DagDashboardResponse;

// DAG Overview API (GET /api/v1/dags/{dag_uuid}/overview) 요청/응답 타입
export type DagOverviewRequest = {
  dagUuid: string;
};

export type DagOverviewResponse = {
  dagUuid: string;
  name: string;
  description?: string | null;
  runType?: string | null;
  nextRun?: string | null;
  lastRun?: string | null;
  deployedVersion?: number | null;
  status: DagListRunStatus;
  tags?: string[];
  maturity: string;
  owner: string;
  latestVersion?: number | null;
  schedule?: string | null;
  entityType?: string | null;
  dataSource?: string | null;
};

// DAG Task Graph API (GET /api/v1/dags/{dag_uuid}/tasks/graph) 요청/응답 타입
export type DagTaskGraphRequest = {
  dagUuid: string;
};

export type DagTaskGraphNode = {
  taskId: string;
  operator: string;
  status?: AirflowTaskInstanceState;
};

export type DagTaskGraphEdge = {
  source: string;
  target: string;
};

export type DagTaskGraphResponse = {
  nodes: DagTaskGraphNode[];
  edges: DagTaskGraphEdge[];
};

// DAG Task List API (GET /api/v1/dags/{dag_uuid}/tasks) 요청/응답 타입
export type DagTaskListRequest = {
  dagUuid: string;
};

export type DagTaskResponse = {
  taskId: string;
  operatorName: string;
  function?: string | null;
  triggerRule: string;
  lastInstance?: string | null;
  lastStatus?: AirflowTaskInstanceState;
};

export type DagTaskListResponse = {
  tasks: DagTaskResponse[];
  total: number;
};

// DAG Execution List API (GET /api/v1/dags/{dag_uuid}/executions) 요청/응답 타입
export type DagExecutionListRequest = {
  dagUuid: string;
  limit?: number;
  offset?: number;
};

export type DagExecutionTaskInstance = {
  taskId: string;
  state?: AirflowTaskInstanceState;
  startDate?: string;
  endDate?: string;
  duration?: number;
  operator: string;
  tryNumber: number;
  triggerRule: string;
};

export type DagExecutionRecord = {
  runId: string;
  dagId: string;
  logicalDate: string;
  startDate?: string;
  endDate?: string;
  duration?: number;
  state: DagListRunStatus;
  runType: string;
  version?: number;
  taskInstances?: DagExecutionTaskInstance[];
};

export type DagExecutionListResponse = {
  items: DagExecutionRecord[];
  total: number;
  limit: number;
  offset: number;
};

// DAG Execution Chart API (GET /api/v1/dags/{dag_uuid}/executions/chart) 요청/응답 타입
export type DagExecutionChartRequest = {
  dagUuid: string;
  limit?: number;
  offset?: number;
};

export type DagExecutionChartRun = {
  runId: string;
  logicalDate: string;
  duration?: number | null;
  state: DagTaskStatus;
};

export type DagExecutionChartResponse = {
  operators: string[];
  runs: DagExecutionChartRun[];
  matrix: string[][];
  total: number;
  limit: number;
  offset: number;
};

export type DagTaskLogStreamRequest = {
  dagUuid: string;
  dagRunId: string;
  taskId?: string;
  tryNumber?: number;
};

export type DagTaskLogStreamEvent = {
  event: string;
  data: string;
};

export type DagTaskLogRequest = {
  dagUuid: string;
  dagRunId: string;
  taskId?: string;
  tryNumber?: number;
};

export type DagTaskLogResponse = {
  logs: {
    task_id: string;
    log: string;
  }[];
};

// DAG Version Code API (GET /api/v1/dags/{dag_uuid}/versions/{version}) 요청/응답 타입
export type DagVersionCodeRequest = {
  dagUuid: string;
  version: number;
};

export type ImportedLibrary = {
  module: string;
  alias?: string;
};

export type DagInstalledLibrary =
  | {
      name: string;
      type: 'standard';
    }
  | {
      name: string;
      type: 'custom';
      content?: string;
    };

export type DagVersionCodeResponse = {
  version: number;
  code: string;
  schedule?: string;
  nextRun?: string;
  lastRun?: string;
  status?: string;
  installedLibraries?: DagInstalledLibrary[];
};

// DAG Template Code API (GET /api/v1/dags/templates/{maturity}) 요청/응답 타입
export type DagTemplateCodeRequest = {
  dagUuid: string;
};

export type DagTemplateCodeResponse = {
  maturity: DagMaturity;
  code: string;
};

// DAG Version List API (GET /api/v1/dags/{dag_uuid}/versions) 요청/응답 타입
export type DagVersionListRequest = {
  dagUuid: string;
};

export type DagVersionResponse = {
  version: number;
  codeHash: string;
  storagePath: string;
  createdBy: string;
  createdAt: string;
  status?: DagListRunStatus;
};

export type DagVersionListResponse = {
  items: DagVersionResponse[];
};

// DAG Save Version API (POST /api/v1/dags/{dag_uuid}/versions) 요청/응답 타입
export type SaveDagVersionRequest = {
  dagUuid: string;
  code: string;
  installedLibraries?: DagInstalledLibrary[];
};

export type SaveDagVersionResponse = {
  version: number;
  codeHash: string;
  storagePath: string;
  createdBy: string;
  createdAt: string;
  status?: DagListRunStatus;
};

// DAG Deploy API (POST /api/v1/dags/{dag_uuid}/deploy) 요청/응답 타입
export type DeployDagRequest = {
  dagUuid: string;
  version: number;
};

export type DeployDagResponse = {
  deployedVersion: number;
};

// DAG 생성 API (POST /api/v1/dags) 요청 타입
export type CreateDagRequest = {
  name: string;
  maturity: DagMaturity;
  entityType?: string | null;
  dataSourceId?: string | null;
  description?: string | null;
  tags: string[];
};

export type DeleteDagRequest = {
  dagUuid: string;
};

// Toggle DAG Bookmark API (POST /api/v1/dags/{dag_uuid}/bookmark) 요청 타입
export type ToggleDagBookmarkRequest = {
  dagUuid: string;
};

export type ToggleDagBookmarkResponse = {
  isBookmarked: boolean;
};

// Trigger DAG API (POST /api/v1/dags/{dag_uuid}/trigger) 요청 타입
export type TriggerDagRequest = {
  dagUuid: string;
  version?: number | 'initialized' | null;
  deployed?: boolean | null;
  code?: string | null;
};

export type TriggerDagResponse = {
  runId: string;
  status?: string;
};

// Toggle DAG Scheduling API (PATCH /api/v1/dags/{dag_uuid}/scheduling) 요청 타입
export type ToggleDagSchedulingRequest = {
  dagUuid: string;
  enabled: boolean;
};

// Airflow DAG Structure API (GET /structure/structure_data) 요청 타입
export type AirflowDagStructureQueryParams = {
  dagId: string;
};

// Airflow DAG Structure API (GET /structure/structure_data) 응답 타입
export type AirflowDagStructureNode = {
  asset_condition_type?: string;
  children?: AirflowDagStructureNode[];
  id: string;
  is_mapped?: boolean;
  label: string;
  operator?: string;
  setup_teardown_type?: string;
  tooltip?: string;
  type?: string;
};

export type AirflowDagStructureEdge = {
  is_setup_teardown?: string;
  is_source_asset?: string;
  label?: string;
  source_id?: string;
  target_id?: string;
};

export type AirflowDagStructureResponse = {
  nodes: AirflowDagStructureNode[];
  edges: AirflowDagStructureEdge[];
};

export type AirflowDagTag = {
  name: string;
  dag_id?: string;
  dag_display_name?: string;
};

export type AirflowImportErrorListResponse = {
  total_entries: number;
  import_errors: {
    import_error_id?: number;
    filename?: string;
    stack_trace?: string;
    timestamp?: string;
  }[];
};

export type AirflowDagRunSummary = {
  start_date?: string;
  logical_date?: string;
  run_after?: string;
  state?: string;
};

export type AirflowDagVersionSnapshot = {
  bundle_version: string;
  description?: string;
  timetable_summary?: string;
  tags: string[];
  run_status?: AirflowDagListRunStatus;
  last_run_at?: string;
  next_dagrun_run_after?: string;
};

export type AirflowDagListItem = {
  dag_id: string;
  dag_display_name?: string;
  is_paused: boolean;
  description?: string;
  timetable_summary?: string;
  timetable_description?: string;
  owners?: string[];
  tags?: AirflowDagTag[];
  has_import_errors: boolean;
  next_dagrun_logical_date?: string;
  latest_dag_runs?: AirflowDagRunSummary[];
  version_snapshots?: AirflowDagVersionSnapshot[];
};

export type AirflowDagListResponse = {
  total_entries: number;
  dags: AirflowDagListItem[];
};

/** Airflow DAG 상세 API (GET /dags/{dag_id}) 응답 타입 */
export type AirflowDagDetailResponse = {
  dag_id: string;
  dag_display_name?: string;
  is_paused: boolean;
  is_stale?: boolean;
  last_parsed_time?: string;
  last_parse_duration?: number;
  last_expired?: string;
  bundle_name?: string;
  bundle_version?: string;
  relative_fileloc?: string;
  fileloc?: string;
  description?: string;
  timetable_summary?: string;
  timetable_description?: string;
  tags?: AirflowDagTag[];
  max_active_tasks?: number;
  max_active_runs?: number;
  max_consecutive_failed_dag_runs?: number;
  has_task_concurrency_limits?: boolean;
  has_import_errors: boolean;
  next_dagrun_logical_date?: string;
  next_dagrun_data_interval_start?: string;
  next_dagrun_data_interval_end?: string;
  next_dagrun_run_after?: string;
  owners?: string[];
  file_token?: string;
};

export type AirflowDagPatchBody = {
  is_paused: boolean;
};

/** Airflow Latest Run API (GET /ui/dags/{dag_id}/latest_run) 응답 타입 */
export type AirflowLatestRunResponse = {
  id: number;
  dag_id: string;
  run_id: string;
  logical_date?: string;
  run_after?: string;
  start_date?: string;
  end_date?: string;
  state?: AirflowDagRunState;
  duration?: number;
};

/** Airflow DAG Grid Runs API (GET /ui/grid/runs/{dag_id}) 응답 아이템 타입 */
export type AirflowDagGridRun = {
  dag_id: string;
  duration?: number;
  end_date?: string;
  queued_at?: string;
  run_after: string;
  run_id: string;
  run_type: string;
  start_date?: string;
  state: AirflowDagRunState;
};

export type AirflowDagGridRunsResponse = AirflowDagGridRun[];

export type AirflowTaskTimeDelta = {
  __type: 'TimeDelta';
  days: number;
  seconds: number;
  microseconds: number;
};

export type AirflowDagTask = {
  task_id: string;
  task_display_name: string;
  owner: string;
  start_date: string;
  end_date: string;
  trigger_rule: string;
  depends_on_past: boolean;
  wait_for_downstream: boolean;
  retries: number;
  queue: string;
  pool: string;
  pool_slots: number;
  execution_timeout: AirflowTaskTimeDelta;
  retry_delay: AirflowTaskTimeDelta;
  retry_exponential_backoff: boolean;
  priority_weight: number;
  weight_rule: string;
  ui_color: string;
  ui_fgcolor: string;
  template_fields: string[];
  downstream_task_ids: string[];
  doc_md: string;
  operator_name: string;
  params: Record<string, unknown>;
  class_ref: Record<string, unknown>;
  is_mapped: boolean;
  extra_links: string[];
};

/** Airflow DAG Tasks API (GET /dags/{dag_id}/tasks) 응답 타입 */
export type AirflowDagTasksResponse = {
  tasks: AirflowDagTask[];
  total_entries: number;
};

export type AirflowTaskInstanceSummaryChildStates = Partial<Record<Exclude<AirflowTaskInstanceState, null>, number>>;

export type AirflowDagTaskInstanceSummary = {
  task_id: string;
  state: AirflowTaskInstanceState;
  child_states?: AirflowTaskInstanceSummaryChildStates;
  min_start_date?: string;
  max_end_date?: string;
};

export type AirflowDagTaskInstanceSummaryResponse = {
  run_id: string;
  dag_id: string;
  task_instances: AirflowDagTaskInstanceSummary[];
};

export type AirflowDagVersion = {
  id: string;
  version_number: number;
  dag_id: string;
  bundle_name: string | null;
  bundle_version: string | null;
  created_at: string;
  dag_display_name: string;
  bundle_url: string | null;
};

export type AirflowDagRunTriggeredByType =
  | 'cli'
  | 'operator'
  | 'rest_api'
  | 'ui'
  | 'test'
  | 'timetable'
  | 'asset'
  | 'backfill';

export type AirflowDagRunListItem = {
  dag_run_id: string;
  dag_id: string;
  logical_date: string;
  queued_at: string | null;
  start_date: string | null;
  end_date: string | null;
  duration: number | null;
  data_interval_start: string | null;
  data_interval_end: string | null;
  run_after: string;
  last_scheduling_decision: string | null;
  run_type: string;
  state: AirflowDagRunState;
  triggered_by: AirflowDagRunTriggeredByType | null;
  triggering_user_name: string | null;
  conf: Record<string, unknown> | null;
  note: string | null;
  dag_versions: AirflowDagVersion[];
  bundle_version: string | null;
  dag_display_name: string;
};

export type AirflowDagRunListResponse = {
  dag_runs: AirflowDagRunListItem[];
  total_entries: number;
};

export type AirflowTaskInstance = {
  id: string;
  task_id: string;
  dag_id: string;
  dag_run_id: string;
  map_index: number;
  logical_date: string | null;
  run_after: string;
  start_date: string | null;
  end_date: string | null;
  duration: number | null;
  state: AirflowTaskInstanceState;
  try_number: number;
  max_tries: number;
  task_display_name: string;
  dag_display_name: string;
  hostname: string | null;
  unixname: string | null;
  pool: string;
  pool_slots: number;
  queue: string | null;
  priority_weight: number | null;
  operator: string | null;
  operator_name: string | null;
  queued_when: string | null;
  scheduled_when: string | null;
  pid: number | null;
  executor: string | null;
  executor_config: string;
  note: string | null;
  rendered_map_index: string | null;
  rendered_fields: Record<string, unknown>;
  trigger: Record<string, unknown> | null;
  triggerer_job: Record<string, unknown> | null;
  dag_version: AirflowDagVersion | null;
};

export type AirflowTaskInstancesResponse = {
  task_instances: AirflowTaskInstance[];
  total_entries: number;
};
